/*
 ** Author: Mika Dekker (AskMario)
 ** Date: 09/2025
 ** Test: GLX-043 PLP: Slider Toggle
 ** Variation: AskMario
*/

/*
  Script Documentation: PLP Swiper Toggle (GLX-043)

  This script is designed as an A/B test variation for a Shopify Product Listing Page (PLP), likely for the Prestige theme. 
  Its primary goal is to enhance the user experience on mobile and tablet devices by converting static product images into 
  an interactive image carousel.

  Core Functionality 
  - Responsive Image Slider: On viewports 1024px or narrower (tablets and mobile devices), it replaces the default product 
    card image with a Swiper.js carousel. This allows users to swipe through the first few product images directly from the PLP.

  - Variant Image Sync: The image carousel is synchronized with the product's color swatches. When a user selects a different 
    color variant, the images in the carousel update to show the images associated with that specific variant.

  - Desktop Fallback: On viewports wider than 1024px (desktops), the script does nothing on initial load, leaving the default 
    theme behavior intact. If the screen is resized from mobile to desktop, the script gracefully removes the Swiper instance 
    and restores the original static image.

  - Performance Optimized: It uses an IntersectionObserver to lazy-load and initialize the swiper functionality only for 
    product cards that are visible or about to become visible on the screen. This prevents performance degradation from 
    initializing dozens of sliders at once.

  How It Works 
  The script follows a logical flow to apply these changes without interfering with the site's base code.

  1. Initialization and Setup
     - Self-Contained Execution: The entire script is wrapped in an (async () => { ... })(); block. This is a standard practice 
       to create a private scope, preventing its variables and functions from conflicting with other scripts on the page 
       (like jQuery or theme scripts).

     - Execution Guard: It first checks if the test has already run by looking for a class on the <html> element (.GLX-043_var_1). 
       If found, it stops, preventing the script from running multiple times.

     - Dependency Loading: It dynamically injects the Swiper.js library (both CSS and JavaScript) into the page's <head>. 
       This ensures the library is only loaded when the test script is active.

  2. Finding and Processing Product Cards
     - makeChanges(): This function is the main trigger. It selects all <product-card> elements on the page.

     - IntersectionObserver: Instead of initializing a slider for every product at once, it "watches" for each product card to 
       enter the viewport. When a card is about to be seen by the user, it calls initializeProductCard() for that specific card.

     - MutationObserver: The script also watches for changes to the page's DOM. This ensures that if new products are loaded 
       via "infinite scroll" or a "Load More" button, they will also get the swiper functionality.

  3. The Core Logic: initializeProductCard()
     This is the most important function, responsible for the logic of each individual product card.

     - State Caching: As soon as it runs, it finds and saves a reference to the original image container (.product-card__media). 
       This is the key to the bug fix, as it allows the script to restore the original state on desktop.

     - Responsive Check (initSwiperForViewport): It checks the window width using window.matchMedia.

       If Desktop: It ensures no swiper is active. If one was active (due to resizing), it calls swiper.destroy() and then 
       replaces the swiper's HTML with the saved original image container.

       If Mobile/Tablet: It builds the necessary HTML structure for a Swiper carousel. It then replaces the original image 
       container in the DOM with this new Swiper structure.

     - Data Fetching: It uses the product's handle to fetch detailed product data from Shopify's JSON API (/products/{handle}.js). 
       This JSON contains the URLs for all product images and variant information.

     - Swiper Initialization (initializeVariantSwiper):
       - It populates the Swiper with the images for the default variant (limited to 3 images).
       - It creates a new Swiper instance with options for looping and pagination.
       - It adds a change event listener to the color swatches. When a swatch is clicked, this function runs again, finds the 
         images for the newly selected variant, clears the old slides, adds the new ones, and updates the swiper.

     - Resize Handling: An event listener is attached to the window's resize event. This listener calls the responsive check 
       (initSwiperForViewport) again, allowing the page to seamlessly switch between the static image view and the Swiper 
       carousel as the browser window is resized.

  Execution Flow Summary 
  - Script Starts: The IIFE runs.
  - Wait for Page: waitForElement('body') pauses execution until the basic page structure is ready.
  - Load Swiper: The swiperJs() function injects the Swiper.js CSS and JS files.

  Once Swiper is Loaded:
  - The makeChanges() function is called.
  - It finds all <product-card> elements.
  - The IntersectionObserver starts watching them.

  As User Scrolls:
  - When a product card comes into view, initializeProductCard() is triggered for it.
  - The responsive logic runs, the DOM is modified for mobile/tablet, and the Swiper is initialized.

  User Interaction:
  - Clicking a color swatch refetches images and updates the slides.
  - Resizing the browser window toggles between the Swiper and the original static image.
*/


(async () => {
  const debugMode = 1,
    testId = "GLX-043",
    logInfo = testId + " -->",
    variation = 1;

  try {
    if (document.documentElement.classList.contains(`${testId}_var_${variation}`)) return;

    document.documentElement.classList.add(`${testId}_var_${variation}`);
    document.documentElement.classList.add(`${testId}_var`);

    let _window = window;
    if (typeof unsafeWindow !== "undefined") _window = unsafeWindow;
    _window._conv_q = _window._conv_q || [];

    const waitForElement = (selector) =>
      new Promise((resolve) => {
        if (document.querySelector(selector)) return resolve(document.querySelector(selector));
        const observer = new MutationObserver(() => {
          if (document.querySelector(selector)) {
            observer.disconnect();
            resolve(document.querySelector(selector));
          }
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
      });

    const loadJavascriptFile = (url, callback) => {
      const script = document.createElement("script");
      script.onload = callback;
      script.src = url;
      document.head.appendChild(script);
    };

    const swiperJs = (callback) => {
      const swiperCSS = document.createElement("link");
      swiperCSS.rel = "stylesheet";
      swiperCSS.href = "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css";
      document.head.appendChild(swiperCSS);

      loadJavascriptFile("https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js", callback);
    };

    const makeChanges = () => {
      const productCards = document.querySelectorAll("product-card");
      const observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              initializeProductCard(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "600px" }
      );
      productCards.forEach((pc) => observer.observe(pc));
    };

    const fetchProductData = async (handle) => {
      try {
        const res = await fetch(`/products/${handle}.js`);
        return await res.json();
      } catch (err) {
        if (debugMode) console.error(logInfo, "Fetch failed", err);
        return null;
      }
    };

    const initializeProductCard = (productCard) => {
      let currentSwiper = null;
      // Store the original image container at the beginning.
      const originalImageContainer = productCard.querySelector(".product-card__media");
    
      // If the original container doesn't exist, we can't do anything.
      if (!originalImageContainer) return;
    
      const initSwiperForViewport = () => {
        const isMobileTablet = window.matchMedia("(max-width: 1024px)").matches;
    
        if (!isMobileTablet) {
          // DESKTOP LOGIC
          // If a Swiper instance exists, destroy it.
          if (currentSwiper) {
            currentSwiper.destroy(true, true);
            currentSwiper = null;
          }
          
          // Check if the swiper container is currently in the DOM.
          const swiperMediaContainer = productCard.querySelector(`.${testId}-media-container`);
          if (swiperMediaContainer) {
            // Restore the original image container.
            swiperMediaContainer.parentNode.replaceChild(originalImageContainer, swiperMediaContainer);
          }
          return; // Exit function for desktop.
        }
    
        // MOBILE & TABLET LOGIC
        // If the swiper is already initialized, do nothing.
        if (productCard.querySelector(`.${testId}-swiper`)) return;
    
        const productHandle = productCard.getAttribute("handle");
        if (!productHandle) return;
    
        // Use the stored original container reference.
        const imageContainer = originalImageContainer;
        
        const pdpUrl =
          imageContainer.tagName === "A"
            ? imageContainer.getAttribute("href")
            : `/products/${productHandle}`;
    
        const newContainer = document.createElement("a");
        newContainer.className = `${testId}-media-container`;
        newContainer.href = pdpUrl;
        newContainer.draggable = false;
    
        if (imageContainer.tagName === "A" && imageContainer.hasAttribute("aria-label")) {
          newContainer.setAttribute("aria-label", imageContainer.getAttribute("aria-label"));
        }
    
        const swiperContainer = document.createElement("div");
        swiperContainer.className = `swiper ${testId}-swiper`;
        const swiperWrapper = document.createElement("div");
        swiperWrapper.className = "swiper-wrapper";
        const swiperPagination = document.createElement("div");
        swiperPagination.className = `swiper-pagination ${testId}-pagination`;
    
        swiperContainer.appendChild(swiperWrapper);
        swiperContainer.appendChild(swiperPagination);
        newContainer.appendChild(swiperContainer);
    
        // Use the stored original container for the replacement.
        imageContainer.parentNode.replaceChild(newContainer, imageContainer);
    
        const colorSwatches = productCard.querySelectorAll('input[type="radio"]');
    
        fetchProductData(productHandle).then((productData) => {
          if (!productData) return;
          const defaultVariant = productData.variants[0];
          initializeVariantSwiper(defaultVariant.id, productData);
          
          colorSwatches.forEach((swatch) => {
            swatch.addEventListener("change", () => {
              const variantId = swatch.getAttribute("data-variant-id");
              newContainer.href = `/products/${productHandle}?variant=${variantId}`;
              initializeVariantSwiper(variantId, productData);
            });
          });
        });
    
        function initializeVariantSwiper(variantId, productData) {
          swiperWrapper.innerHTML = "";
          const variant = productData.variants.find((v) => v.id == variantId);
          if (!variant) return;
    
          let imgs = getVariantImages(variant, productData);
          imgs = imgs.slice(0, 3);
          if (!imgs.length) return;
    
          imgs.forEach((src) => {
            const slide = document.createElement("div");
            slide.className = "swiper-slide";
            const img = document.createElement("img");
            img.src = src;
            img.alt = productData.title;
            img.loading = "lazy";
            slide.appendChild(img);
            swiperWrapper.appendChild(slide);
          });
    
          if (currentSwiper) currentSwiper.destroy(true, true);
    
          currentSwiper = new Swiper(swiperContainer, {
            loop: imgs.length > 1,
            slidesPerView: 1,
            pagination: {
              el: swiperPagination,
              clickable: true,
            },
          });
        }
    
        function getVariantImages(variant, productData) {
          const out = [];
          const pushUnique = (src) => {
            if (src && !out.includes(src)) out.push(src);
          };
          if (variant.featured_image?.src) pushUnique(variant.featured_image.src);
          if (productData.images?.length) {
            productData.images.forEach((img) => pushUnique(img));
          }
          return out;
        }
      };
    
      // Run on load + bind resize listener
      initSwiperForViewport();
      window.addEventListener("resize", initSwiperForViewport);
    };

    waitForElement("body").then(() =>
      swiperJs(() => {
        new MutationObserver(makeChanges).observe(document.body, { childList: true, subtree: true });
        makeChanges();
      })
    );
  } catch (err) {
    console.error("Init failed", err);
  }
})();
